import type { LyrraOnePublicRegion } from '@/lib/lyrraone-public-region';
import type { Language } from '@/lib/language-prefs';
import { parseSupportedUiLangCode } from '@/lib/ui-languages';
import {
  getMarketLandingBundle,
  getMarketPricingBundle,
} from '@/lib/lyrraone-market-config';

export type LyrraOneRegionalNamespace =
  | 'lyrraoneLanding'
  | 'lyrraonePricing'
  | 'lyrraoneHeader'
  | 'lyrraoneFooter'
  | 'lyrraoneMarketplace'
  | 'lyrraoneAccessibility'
  | 'lyrraoneContact'
  | 'lyrraoneAuth'
  | 'lyrraoneFeatures'
  | 'lyrraoneDemo'
  | 'lyrraonePrivacy'
  | 'lyrraoneTerms';

/** Répertoires JSON sous `src/i18n/translations/`. */
const NAMESPACE_DIRS: Record<LyrraOneRegionalNamespace, string> = {
  lyrraoneLanding: 'lyrraone-landing',
  lyrraonePricing: 'lyrraone-pricing',
  lyrraoneHeader: 'public-header',
  lyrraoneFooter: 'public-footer',
  lyrraoneMarketplace: 'lyrraone-marketplace',
  lyrraoneAccessibility: 'lyrraone-accessibility',
  lyrraoneContact: 'lyrraone-contact',
  lyrraoneAuth: 'lyrraone-auth',
  lyrraoneFeatures: 'lyrraone-features',
  lyrraoneDemo: 'lyrraone-demo',
  lyrraonePrivacy: 'lyrraone-privacy',
  lyrraoneTerms: 'lyrraone-terms',
};

/** Fichiers header/footer LyrraOne : `lyrraone-{lang}.json`. */
const LYRRAONE_PREFIX_NAMESPACES = new Set<LyrraOneRegionalNamespace>(['lyrraoneHeader', 'lyrraoneFooter']);

const translationModules = import.meta.glob('../i18n/translations/**/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, unknown>;

function resolveDotPath(obj: unknown, key: string): unknown {
  let value: unknown = obj;
  for (const part of key.split('.')) {
    if (value && typeof value === 'object' && part in (value as Record<string, unknown>)) {
      value = (value as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return value;
}

function resolveUiLanguage(language?: string | null): Language {
  return parseSupportedUiLangCode(language) ?? 'en';
}

function fileNameFor(namespace: LyrraOneRegionalNamespace, lang: string): string {
  if (LYRRAONE_PREFIX_NAMESPACES.has(namespace)) return `lyrraone-${lang}.json`;
  return `${lang}.json`;
}

function loadBundle(namespace: LyrraOneRegionalNamespace, lang: string): unknown | undefined {
  const dir = NAMESPACE_DIRS[namespace];
  const file = fileNameFor(namespace, lang);
  const suffix = `/i18n/translations/${dir}/${file}`;
  const entry = Object.entries(translationModules).find(([path]) => path.endsWith(suffix));
  return entry?.[1];
}

function readKey(bundle: unknown, key: string): string | null {
  const value = resolveDotPath(bundle, key);
  if (typeof value === 'string' || typeof value === 'boolean') return String(value);
  return null;
}

/**
 * Résout une clé marketing LyrraOne selon la langue UI (sélecteur header),
 * avec repli en → fr et variante Maroc (ma) pour le français.
 */
export function getLyrraOneRegionalValue(
  namespace: LyrraOneRegionalNamespace,
  region: LyrraOnePublicRegion,
  key: string,
  language?: string | null,
): string {
  const lang = resolveUiLanguage(language);

  const candidates: string[] = [];

  if (namespace === 'lyrraonePricing') {
    const pricingBundle = getMarketPricingBundle(region);
    if (pricingBundle) candidates.push(pricingBundle);
  }

  if (namespace === 'lyrraoneLanding') {
    const landingBundle = getMarketLandingBundle(region, lang);
    if (landingBundle) candidates.push(landingBundle);
  }

  candidates.push(lang, 'en', 'fr');

  const seen = new Set<string>();
  for (const code of candidates) {
    if (seen.has(code)) continue;
    seen.add(code);
    const bundle = loadBundle(namespace, code);
    const value = bundle ? readKey(bundle, key) : null;
    if (value !== null) return value;
  }

  return key;
}

export interface LyrraOnePricingComparisonItem {
  name: string;
  free: string | boolean;
  pro: string | boolean;
  expert: string | boolean;
}

export interface LyrraOnePricingComparisonCategory {
  label: string;
  items: LyrraOnePricingComparisonItem[];
}

function loadPricingBundle(region: LyrraOnePublicRegion, language?: string | null): unknown | undefined {
  const lang = resolveUiLanguage(language);
  const candidates: string[] = [];
  const pricingBundle = getMarketPricingBundle(region);
  if (pricingBundle) candidates.push(pricingBundle);
  candidates.push(lang, 'en', 'fr');

  const seen = new Set<string>();
  for (const code of candidates) {
    if (seen.has(code)) continue;
    seen.add(code);
    const bundle = loadBundle('lyrraonePricing', code);
    if (bundle) return bundle;
  }
  return undefined;
}

/** Tableau comparatif tarifs — lecture directe du JSON i18n (structure `comparison.categories`). */
export function getLyrraOnePricingComparison(
  region: LyrraOnePublicRegion,
  language?: string | null,
): { title: string; categories: LyrraOnePricingComparisonCategory[] } | null {
  const bundle = loadPricingBundle(region, language) as {
    comparison?: {
      title?: string;
      categories?: Record<string, LyrraOnePricingComparisonCategory>;
    };
  } | undefined;

  const comparison = bundle?.comparison;
  if (!comparison?.categories) return null;

  const categories = Object.values(comparison.categories).filter(
    (cat): cat is LyrraOnePricingComparisonCategory =>
      Boolean(cat?.label && Array.isArray(cat.items) && cat.items.length > 0),
  );

  if (!categories.length) return null;

  return {
    title: comparison.title ?? 'Comparer les formules',
    categories,
  };
}
